import ExcelJS from 'exceljs';
import { branding } from 'stripes-config';

export const exportClearanceReportToExcel = async (data, filename, translations, statistics) => {
  try {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'FOLIO Users Clearance Report';
    workbook.created = new Date();

    // Add cover sheet
    const coverSheet = workbook.addWorksheet('Cover Page');
    
    coverSheet.columns = [
      { width: 8 }, { width: 15 }, { width: 20 }, { width: 25 },
      { width: 25 }, { width: 20 }, { width: 15 }, { width: 8 },
    ];

    // Header section with blue background
    for (let row = 10; row <= 22; row++) {
      for (let col = 3; col <= 6; col++) {
        const cell = coverSheet.getCell(row, col);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF106BA3' },
        };
      }
    }

    // Add logo if available
    if (branding?.logo?.src) {
      try {
        const logoResponse = await fetch(branding.logo.src);
        const logoBuffer = await logoResponse.arrayBuffer();
        const logoId = workbook.addImage({
          buffer: logoBuffer,
          extension: 'png',
        });
        
        coverSheet.addImage(logoId, {
          tl: { col: 3.2, row: 10.8 },
          ext: { width: 180, height: 120 },
        });
      } catch (error) {
        console.warn('Could not load logo:', error);
      }
    }

    // Platform title
    coverSheet.mergeCells('C15:F16');
    const titleMergedCell = coverSheet.getCell('C15');
    titleMergedCell.value = translations.platformTitle || 'منصة الفوليو العربي';
    titleMergedCell.font = { size: 32, bold: true, color: { argb: 'FFFFFFFF' } };
    titleMergedCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    
    coverSheet.mergeCells('C17:F18');
    const moduleMergedCell = coverSheet.getCell('C17');
    moduleMergedCell.value = translations.moduleTitle || 'تقارير المستخدمين';
    moduleMergedCell.font = { size: 16, color: { argb: 'FFE8F4FD' } };
    moduleMergedCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    // Report title
    const reportTitleCell = coverSheet.getCell('D24');
    reportTitleCell.value = filename;
    reportTitleCell.font = { size: 28, bold: true, color: { argb: 'FF106BA3' } };
    reportTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    reportTitleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF8F9FA' },
    };

    // Information section
    const now = new Date();
    const infoData = [
      [translations.generatedDate || 'تاريخ التوليد:', now.toLocaleDateString()],
      [translations.generatedTime || 'وقت التوليد:', now.toLocaleTimeString()],
      [translations.totalRecords || 'إجمالي السجلات:', data.length.toLocaleString()],
      [translations.reportType || 'نوع التقرير:', translations.reportTypeValue || 'تقرير إخلاءات الطرف'],
    ];

    infoData.forEach((info, index) => {
      const row = 28 + index;
      const labelCell = coverSheet.getCell(`C${row}`);
      labelCell.value = info[0];
      labelCell.font = { size: 14, bold: true, color: { argb: 'FF333333' } };
      labelCell.alignment = { horizontal: 'right', vertical: 'middle' };
      labelCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8F4FD' },
      };

      const valueCell = coverSheet.getCell(`E${row}`);
      valueCell.value = info[1];
      valueCell.font = { size: 14, color: { argb: 'FF333333' } };
      valueCell.alignment = { horizontal: 'left', vertical: 'middle' };
      valueCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFFFF' },
      };
    });

    // Statistics section
    if (statistics) {
      const statsStartRow = 34;
      const statsTitle = coverSheet.getCell(`D${statsStartRow}`);
      statsTitle.value = translations.statistics || 'الإحصائيات';
      statsTitle.font = { size: 18, bold: true, color: { argb: 'FF106BA3' } };
      statsTitle.alignment = { horizontal: 'center', vertical: 'middle' };

      let currentRow = statsStartRow + 2;
      
      if (statistics.byLibrary) {
        const libTitle = coverSheet.getCell(`C${currentRow}`);
        libTitle.value = translations.byLibrary || 'حسب المكتبة:';
        libTitle.font = { size: 12, bold: true };
        currentRow++;

        Object.entries(statistics.byLibrary).forEach(([lib, count]) => {
          const libCell = coverSheet.getCell(`D${currentRow}`);
          libCell.value = `${lib}: ${count}`;
          libCell.font = { size: 11 };
          currentRow++;
        });
        currentRow++;
      }

      if (statistics.byStaff) {
        const staffTitle = coverSheet.getCell(`C${currentRow}`);
        staffTitle.value = translations.byStaff || 'حسب الموظف:';
        staffTitle.font = { size: 12, bold: true };
        currentRow++;

        Object.entries(statistics.byStaff).forEach(([staff, count]) => {
          const staffCell = coverSheet.getCell(`D${currentRow}`);
          staffCell.value = `${staff}: ${count}`;
          staffCell.font = { size: 11 };
          currentRow++;
        });
      }
    }

    // Set row heights
    coverSheet.getRow(15).height = 35;
    coverSheet.getRow(16).height = 35;
    coverSheet.getRow(17).height = 25;
    coverSheet.getRow(18).height = 25;
    coverSheet.getRow(24).height = 45;
    for (let i = 28; i <= 33; i++) {
      coverSheet.getRow(i).height = 30;
    }

    // Data worksheet
    const worksheet = workbook.addWorksheet('Clearance Data', {
      pageSetup: { paperSize: 9, orientation: 'landscape' },
    });

    const headers = [
      translations.userName || 'اسم المستخدم',
      translations.barcode || 'الباركود',
      translations.patronGroup || 'مجموعة المستفيد',
      translations.clearanceDate || 'تاريخ الإخلاء',
      translations.library || 'المكتبة',
      translations.clearedBy || 'تم الإخلاء بواسطة',
      translations.expirationDate || 'تاريخ الانتهاء',
    ];

    worksheet.addRow(headers);

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF106BA3' },
    };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow.height = 25;

    data.forEach((row, index) => {
      const dataRow = worksheet.addRow([
        row.userName,
        row.barcode,
        row.patronGroup,
        row.clearanceDate,
        row.library,
        row.clearedBy,
        row.expirationDate,
      ]);

      if (index % 2 === 1) {
        dataRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF8F9FA' },
        };
      }

      dataRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
    });

    worksheet.columns.forEach((column, index) => {
      let maxLength = headers[index].length;
      data.slice(0, 100).forEach((row) => {
        const values = [row.userName, row.barcode, row.patronGroup, row.clearanceDate, row.library, row.clearedBy, row.expirationDate];
        const value = values[index];
        if (value) {
          const displayLength = value.toString().length;
          if (displayLength > maxLength) {
            maxLength = displayLength;
          }
        }
      });
      column.width = Math.min(Math.max(maxLength + 3, 15), 60);
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    worksheet.autoFilter = { from: 'A1', to: 'G1' };

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = `FOLIO_${filename}_${timestamp}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFilename;
    link.click();
    window.URL.revokeObjectURL(url);

    return { success: true, filename: finalFilename, recordCount: data.length };
  } catch (error) {
    console.error('Excel export error:', error);
    return { success: false, error: error.message };
  }
};
